<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import SettingsSection from 'dashboard/components/SettingsSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DurationInput from 'next/input/DurationInput.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const DEFAULT_WARNING_THRESHOLD = 10;
const DEFAULT_CRITICAL_THRESHOLD = 15;

const isOperationTimerEnabled = ref(false);
const warningThreshold = ref(DEFAULT_WARNING_THRESHOLD);
const warningUnit = ref(DURATION_UNITS.MINUTES);
const criticalThreshold = ref(DEFAULT_CRITICAL_THRESHOLD);
const criticalUnit = ref(DURATION_UNITS.MINUTES);
const isSubmitting = ref(false);

// Convert duration to minutes based on unit
const convertToMinutes = (value, unit) => {
  switch (unit) {
    case DURATION_UNITS.HOURS:
      return value * 60;
    case DURATION_UNITS.MINUTES:
    default:
      return value;
  }
};

// Convert minutes to appropriate unit and value
const convertFromMinutes = minutes => {
  if (minutes % 60 === 0 && minutes >= 60) {
    return {
      value: minutes / 60,
      unit: DURATION_UNITS.HOURS,
    };
  }
  return {
    value: minutes,
    unit: DURATION_UNITS.MINUTES,
  };
};

// Load settings from inbox prop
const setDefaults = () => {
  const {
    operation_timer_enabled: isEnabled = false,
    operation_timer_config: config = {},
  } = props.inbox;

  isOperationTimerEnabled.value = isEnabled;

  // Set warning threshold
  const warningMinutes =
    config.warning_threshold_minutes || DEFAULT_WARNING_THRESHOLD;
  const warningConverted = convertFromMinutes(warningMinutes);
  warningThreshold.value = warningConverted.value;
  warningUnit.value = warningConverted.unit;

  // Set critical threshold
  const criticalMinutes =
    config.critical_threshold_minutes || DEFAULT_CRITICAL_THRESHOLD;
  const criticalConverted = convertFromMinutes(criticalMinutes);
  criticalThreshold.value = criticalConverted.value;
  criticalUnit.value = criticalConverted.unit;
};

// Computed values for validation
const warningThresholdMinutes = computed(() =>
  convertToMinutes(warningThreshold.value, warningUnit.value)
);
const criticalThresholdMinutes = computed(() =>
  convertToMinutes(criticalThreshold.value, criticalUnit.value)
);

// Watch inbox prop changes
watch(
  () => props.inbox,
  () => {
    setDefaults();
  },
  { deep: true, immediate: true }
);

const store = useStore();

const updateInboxSettings = async () => {
  if (isOperationTimerEnabled.value) {
    if (criticalThresholdMinutes.value <= warningThresholdMinutes.value) {
      useAlert(
        t('INBOX_MGMT.OPERATION_TIMER.VALIDATION.CRITICAL_GREATER_THAN_WARNING')
      );
      return;
    }
  }

  try {
    isSubmitting.value = true;
    const payload = {
      id: props.inbox.id,
      formData: false,
      operation_timer_enabled: isOperationTimerEnabled.value,
      operation_timer_config: isOperationTimerEnabled.value
        ? {
            warning_threshold_minutes: warningThresholdMinutes.value,
            critical_threshold_minutes: criticalThresholdMinutes.value,
          }
        : {},
      channel: {},
    };

    await store.dispatch('inboxes/updateInbox', payload);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isSubmitting.value = false;
  }
};

const toggleOperationTimer = () => {
  if (!isOperationTimerEnabled.value) {
    // Reset to defaults when disabling
    warningThreshold.value = DEFAULT_WARNING_THRESHOLD;
    warningUnit.value = DURATION_UNITS.MINUTES;
    criticalThreshold.value = DEFAULT_CRITICAL_THRESHOLD;
    criticalUnit.value = DURATION_UNITS.MINUTES;
  }
};
</script>

<template>
  <div class="mx-8">
    <SettingsSection
      :title="$t('INBOX_MGMT.OPERATION_TIMER.TITLE')"
      :sub-title="$t('INBOX_MGMT.OPERATION_TIMER.SUBTITLE')"
    >
      <form @submit.prevent="updateInboxSettings">
        <label
          for="toggle-operation-timer"
          class="flex items-center mb-2 font-medium text-slate-700 dark:text-slate-300"
        >
          <input
            id="toggle-operation-timer"
            v-model="isOperationTimerEnabled"
            type="checkbox"
            class="ltr:mr-2 rtl:ml-2"
            name="toggle-operation-timer"
            aria-describedby="operation-timer-help"
            @change="toggleOperationTimer"
          />
          {{ $t('INBOX_MGMT.OPERATION_TIMER.TOGGLE_AVAILABILITY') }}
        </label>
        <p
          id="operation-timer-help"
          class="mb-4 text-slate-700 dark:text-slate-300"
        >
          {{ $t('INBOX_MGMT.OPERATION_TIMER.TOGGLE_HELP') }}
        </p>

        <div v-if="isOperationTimerEnabled" class="mb-6">
          <div class="grid gap-4 mb-4">
            <WithLabel
              :label="$t('INBOX_MGMT.OPERATION_TIMER.WARNING_THRESHOLD.LABEL')"
              :help-message="
                $t('INBOX_MGMT.OPERATION_TIMER.WARNING_THRESHOLD.HELP')
              "
            >
              <div
                class="gap-2 w-full grid grid-cols-[auto_3fr_1fr] items-center"
              >
                <div class="w-4 h-4 rounded-full bg-yellow-400 flex-shrink-0" />
                <DurationInput
                  v-model="warningThreshold"
                  v-model:unit="warningUnit"
                  min="1"
                  max="1440"
                  class="w-full"
                />
              </div>
            </WithLabel>

            <WithLabel
              :label="$t('INBOX_MGMT.OPERATION_TIMER.CRITICAL_THRESHOLD.LABEL')"
              :help-message="
                $t('INBOX_MGMT.OPERATION_TIMER.CRITICAL_THRESHOLD.HELP')
              "
            >
              <div
                class="gap-2 w-full grid grid-cols-[auto_3fr_1fr] items-center"
              >
                <div class="w-4 h-4 rounded-full bg-n-ruby-4 flex-shrink-0" />
                <DurationInput
                  v-model="criticalThreshold"
                  v-model:unit="criticalUnit"
                  min="1"
                  max="1440"
                  class="w-full"
                />
              </div>
            </WithLabel>
          </div>
        </div>

        <NextButton
          type="submit"
          :label="$t('INBOX_MGMT.OPERATION_TIMER.UPDATE')"
          :is-loading="isSubmitting"
        />
      </form>
    </SettingsSection>
  </div>
</template>
